import { useEffect, useRef, useState } from "react";
import type { DemoScript } from "./marketing-conversations.js";

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function PhoneDemo({ script, hero = false }: { script: DemoScript; hero?: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [visibleCount, setVisibleCount] = useState(() => prefersReducedMotion() ? script.messages.length : 0);
  const [typing, setTyping] = useState(false);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [tabVisible, setTabVisible] = useState(() => typeof document === "undefined" || !document.hidden);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncMotion = () => {
      setReducedMotion(media.matches);
      if (media.matches) {
        setVisibleCount(script.messages.length);
        setTyping(false);
      }
    };
    media.addEventListener("change", syncMotion);
    return () => media.removeEventListener("change", syncMotion);
  }, [script]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
    if (rootRef.current) observer.observe(rootRef.current);
    const syncVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", syncVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", syncVisibility);
    };
  }, []);

  useEffect(() => {
    if (paused || reducedMotion || !inView || !tabVisible) return;
    if (visibleCount === script.messages.length) {
      const restart = window.setTimeout(() => {
        setVisibleCount(0);
        if (chatRef.current) chatRef.current.scrollTop = 0;
      }, hero ? 5500 : 7000);
      return () => window.clearTimeout(restart);
    }

    const next = script.messages[visibleCount];
    if (next.sender === "ai") setTyping(true);
    const delay = visibleCount === 0 ? 850 : next.sender === "ai" ? 2700 : 4700;
    const reveal = window.setTimeout(() => {
      setTyping(false);
      setVisibleCount((count) => count + 1);
    }, delay);
    return () => window.clearTimeout(reveal);
  }, [hero, inView, paused, reducedMotion, script, tabVisible, visibleCount]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: reducedMotion ? "instant" : "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [reducedMotion, typing, visibleCount]);

  const messages = script.messages.slice(0, visibleCount);
  return <div className={`v2-phone ${hero ? "v2-phone-hero" : ""}`} ref={rootRef} aria-label="Conversa comercial fictícia em um celular">
    <div className="v2-phone-screen">
      <div className="v2-phone-status"><span>9:41</span><span className="v2-phone-notch"/><span className="v2-phone-signal">● ▰ ▰</span></div>
      <div className="v2-phone-chat-head"><span className="v2-phone-back">‹</span><span className="v2-phone-avatar">C</span><span className="v2-phone-company"><strong>Clima Exemplo</strong><small>empresa fictícia • demonstração</small></span><span className="v2-phone-more">⋮</span></div>
      <div className="v2-phone-chat-body" ref={chatRef} aria-live="off"><span className="v2-phone-day">HOJE</span>
        {messages.map((message, index) => <div className={`v2-chat-bubble v2-chat-${message.sender === "customer" ? "in" : "out"}`} key={`${script.id}-${index}`}>
          {message.sender === "ai" && <span className="v2-chat-ai"><span>G3</span> G3.AI</span>}
          <p>{message.text}</p><small>10:{String(24 + index).padStart(2, "0")}{message.sender === "ai" && <span aria-hidden="true"> ✓✓</span>}</small>
        </div>)}
        {typing && inView && tabVisible && <div className="v2-chat-bubble v2-chat-out v2-typing" aria-label="G3.AI digitando"><span className="v2-typing-dots"><i/><i/><i/></span><span>digitando...</span></div>}
      </div>
      <div className="v2-phone-compose"><span>Mensagem</span><span className="v2-compose-send" aria-hidden="true">➤</span></div>
    </div>
    <div className="v2-phone-controls"><span>Cenário fictício • sem envio real</span>{reducedMotion ? <span>Movimento reduzido</span> : <button type="button" onClick={() => setPaused((value) => !value)} aria-label={paused ? "Reproduzir demonstração" : "Pausar demonstração"}>{paused ? "▶ Reproduzir" : "Ⅱ Pausar"}</button>}</div>
  </div>;
}
